import "../../styles/animation-conversation.css";
import React, { useRef } from "react";
import mainTheme from "../../theme.tsx";
import { Box, ThemeProvider, useMediaQuery, useTheme } from "@mui/material";
import SearchField from "../search/SearchField.tsx";
import Conversation from "./Conversation.tsx";
import ExampleItems from "../example/ExampleItems.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import ExchangePanel from "./Exchange.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { CONVERSATION_LAYOUT } from "./constants.ts";
import { getSelectedConversationExchanges } from "../../store/conversation.tsx";
import DashboardView from "./DashboardView.tsx";
import MobileAppBar from "./MobileAppBar.tsx";
import DesktopLogoConversationPanel from "./DesktopLogoConversationPanel.tsx";
import Notification from "../notifications/Notification.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";

export interface ConversationPanelProps {
  children?: React.ReactNode;
  openDrawer: boolean;
  setOpenDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
}

const EMPTY_EXCHANGE_IDS: string[] = [];

interface ConversationPanelAnimationRef {
  start: () => void;
  stop: () => void;
}

const ConversationPanel: React.FC<ConversationPanelProps> = (props) => {
  const theme = useTheme();

  // Create ref for the scrollable conversation container
  const conversationContainerRef = useRef<HTMLDivElement>(null);

  const handleDrawerOpen = () => {
    if (props.setOpenDrawer) {
      props.setOpenDrawer(true);
    }
  };

  const { byId: notifications } = useTypedSelector(
    (state) => state.notifications,
  );
  const hasDashboardsFeature = useFeatureFlag("dashboards");

  const selectedConversationExchanges = useTypedSelector(
    getSelectedConversationExchanges,
  );
  const exchangeIds =
    selectedConversationExchanges?.allIds || EMPTY_EXCHANGE_IDS;
  const selectedView = useTypedSelector(
    (state) => state.conversationState.selectedView,
  );

  const refAnimation = useRef<ConversationPanelAnimationRef>(null);

  const { anonymousSession } = useTypedSelector((state) => state.authState);

  // During the sign up flow, we don't show example items below the search bar
  const showExamples = !(
    anonymousSession &&
    anonymousSession.signUpState &&
    !anonymousSession.signUpState.isComplete
  );

  const onPulseAnimation = () => {
    if (refAnimation.current) {
      refAnimation.current.start();
    }
  };

  const stopPulseAnimation = () => {
    if (refAnimation.current) {
      refAnimation.current.stop();
    }
  };

  const isScreenBiggerThanOrEqualToLg: boolean = useMediaQuery(
    theme.breakpoints.up("lg"),
  );
  const isScreenBiggerThanOrEqualToMd: boolean = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  const showNotifications = selectedView === "notifications";
  const showDashboard = hasDashboardsFeature && selectedView === "dashboard";
  const showConversationComposer = selectedView === "conversation";

  return (
    <ThemeProvider theme={mainTheme}>
      <Box>
        {isScreenBiggerThanOrEqualToLg && (
          <DesktopLogoConversationPanel refAnimation={refAnimation} />
        )}
        <Box
          sx={[
            {
              height: "100%",
              width: `min(100vw, 100vh)`,
              // Special-case: slightly narrower on hi-DPI lg screens (devicePixelRatio ≈ 2)
              // This is for example a 14" MacBook Pro with Retina display (3024 × 1964)
              "@media (min-width: 1200px) and (-webkit-min-device-pixel-ratio: 2), (min-width: 1200px) and (min-resolution: 192dpi)":
                {
                  width: "900px",
                },
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              overflow: "hidden",
              position: "fixed",
              top: "50%",
              left: `calc(50%)`,
              backgroundColor: `${theme.palette.secondary.main}`,
              transform: "translate(-50%, -50%)",
              flexDirection: "column",
              marginBottom: "2%",
              transition: theme.transitions.create(["left", "width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
            // If anonymous session, make the panel a bit wider on Mac hi-DPI screens. We do not need to account for the side
            // drawer on the anonymous session.
            anonymousSession && {
              "@media (min-width: 1200px) and (-webkit-min-device-pixel-ratio: 2), (min-width: 1200px) and (min-resolution: 192dpi)":
                {
                  width: "1100px",
                },
            },
            // css for screens greater than md-size
            isScreenBiggerThanOrEqualToMd &&
              props.openDrawer && {
                left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
                transition: theme.transitions.create(["left", "width"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
          ]}
        >
          {/* App Bar for mobile devices - sm and md */}
          {!isScreenBiggerThanOrEqualToLg && (
            <MobileAppBar
              openDrawer={props.openDrawer}
              anonymousSession={!!anonymousSession}
              isScreenBiggerThanOrEqualToLg={isScreenBiggerThanOrEqualToLg}
              handleDrawerOpen={handleDrawerOpen}
            />
          )}
          {/* Scrollable conversation area */}
          <Box
            ref={conversationContainerRef}
            id="conversation-panel-container"
            sx={[
              {
                display: "flex",
                flexDirection: "column",
                flex: 1, // Allow this box to grow and shrink
                width: "100%",
                overflowY: "scroll", // MAIN SCROLLPORT - like ChatGPT
                scrollbarGutter: "stable",
                scrollbarColor: "var(--tally-brown) var(--tally-white)",
                paddingX: {
                  xs: "1.7rem",
                  sm: "2rem",
                  md: "3rem",
                  lg: "5rem",
                  xl: "6rem",
                }, // Horizontal padding to constrain content
                boxSizing: "border-box", // Include padding in width calculation
                marginTop: isScreenBiggerThanOrEqualToLg
                  ? `${SETTINGS.topMargin}px`
                  : `${SETTINGS.topMarginMobile}px`,
                marginBottom: isScreenBiggerThanOrEqualToMd
                  ? showConversationComposer
                    ? `${SETTINGS.bottomMargin}px`
                    : "40px"
                  : showConversationComposer
                    ? `${SETTINGS.bottomMarginMobile}px`
                    : "32px",
              },
            ]}
          >
            {showNotifications ? (
              <Conversation
                scrollContainerRef={
                  conversationContainerRef as React.RefObject<HTMLDivElement>
                }
              >
                {Object.entries(notifications).map(([id, notification]) => (
                  <Notification
                    key={id}
                    id={id}
                    content={notification.message.text}
                    read={notification.read}
                  />
                ))}
              </Conversation>
            ) : showDashboard ? (
              <DashboardView />
            ) : (
              <Conversation
                scrollContainerRef={
                  conversationContainerRef as React.RefObject<HTMLDivElement>
                }
              >
                {exchangeIds.map((exchangeId) => (
                  <ExchangePanel
                    key={exchangeId}
                    exchangeId={exchangeId}
                    onPulseAnimation={onPulseAnimation}
                    stopPulseAnimation={stopPulseAnimation}
                  />
                ))}
              </Conversation>
            )}
          </Box>

          {/* Fixed SearchField at bottom - outside scrollable area */}
          {showConversationComposer && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: theme.palette.secondary.main,
                paddingX: {
                  xs: "1.7rem",
                  sm: "2rem",
                  md: "3rem",
                  lg: "5rem",
                  xl: "6rem",
                }, // Horizontal padding to constrain content
                paddingRight: { lg: "6rem", xl: "7rem" }, // Special right padding to compensate for the scrollbar
                paddingTop: { xs: "20px" },
                paddingBottom: {
                  xs: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.mobile}`
                    : "56px",
                  sm: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.tablet}`
                    : "64px",
                  md: showExamples
                    ? `${CONVERSATION_LAYOUT.bottomPadding.desktop}`
                    : "44px",
                },
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2, md: 2.5 }, // ✅ Responsive spacing: 12px/16px/20px
                alignItems: "center",
              }}
            >
              <SearchField
                onPulseAnimation={onPulseAnimation}
                stopPulseAnimation={stopPulseAnimation}
              />
              {showExamples && (
                <Box sx={{ width: "95%" }}>
                  <ExampleItems />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ConversationPanel;
